import {
  Box,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material'
import { ChartContainer } from '@mui/x-charts/ChartContainer'
import { BarPlot } from '@mui/x-charts/BarChart'
import { LinePlot, MarkPlot } from '@mui/x-charts/LineChart'
import { ChartsXAxis, ChartsYAxis, ChartsLegend, ChartsTooltip } from '@mui/x-charts'
import { useTechDebtMetricsByYear, useTechDebtMetricsLast12Months } from '@src/api/techdebt-api'
import PageLoader from '@src/components/Loader/PageLoader'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

type Period = 'LAST_12M' | '2025'

const TechDebtTab = () => {
  const { t } = useTranslation()
  const [period, setPeriod] = useState<Period>('2025')

  // Fetch data based on selected period
  const [dataByYear, isFetchingYear] = useTechDebtMetricsByYear(2025)
  const [dataLast12Months, isFetchingLast12Months] = useTechDebtMetricsLast12Months()

  const data = period === '2025' ? dataByYear : dataLast12Months
  const isFetching = period === '2025' ? isFetchingYear : isFetchingLast12Months

  // Process data for chart
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return null

    return {
      labels: data.map((m) => {
        const date = new Date(m.date)
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      }),
      carryover: data.map((m) => m.carryoverDebtPoints),
      createdInYear: data.map((m) => m.createdInYearPoints),
      completedInYear: data.map((m) => m.completedInYearPoints),
      totalDebt: data.map((m) => m.totalDebt),
    }
  }, [data])

  const handlePeriodChange = (event: SelectChangeEvent<Period>) => {
    setPeriod(event.target.value as Period)
  }

  if (isFetching) {
    return <PageLoader />
  }

  if (!chartData) {
    return (
      <Box>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>{t('techDebt.period.label')}</InputLabel>
            <Select value={period} onChange={handlePeriodChange} label={t('techDebt.period.label')}>
              <MenuItem value="2025">{t('techDebt.period.2025')}</MenuItem>
              <MenuItem value="LAST_12M">{t('techDebt.period.last12M')}</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Card>
          <CardContent>
            <Typography variant="body1" color="textSecondary">
              {t('techDebt.noData')}
            </Typography>
          </CardContent>
        </Card>
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>{t('techDebt.period.label')}</InputLabel>
          <Select value={period} onChange={handlePeriodChange} label={t('techDebt.period.label')}>
            <MenuItem value="2025">{t('techDebt.period.2025')}</MenuItem>
            <MenuItem value="LAST_12M">{t('techDebt.period.last12M')}</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('techDebt.chart.title')}
          </Typography>
          <Box sx={{ width: '100%', height: 400 }}>
            <ChartContainer
              xAxis={[
                {
                  scaleType: 'band',
                  data: chartData.labels,
                  id: 'x-axis',
                },
              ]}
              yAxis={[{ id: 'y-axis', min: 0 }]}
              series={[
                {
                  type: 'bar',
                  data: chartData.createdInYear,
                  label: t('techDebt.chart.createdInYear'),
                  stack: 'total',
                  color: '#ffd43b',
                  id: 'created',
                },
                {
                  type: 'bar',
                  data: chartData.carryover,
                  label: t('techDebt.chart.carryover'),
                  stack: 'total',
                  color: '#ff6b6b',
                  id: 'carryover',
                },
                {
                  type: 'line',
                  data: chartData.completedInYear,
                  label: t('techDebt.chart.completedInYear'),
                  color: '#51cf66',
                  id: 'completed',
                  curve: 'linear',
                },
              ]}
              height={400}
              margin={{ bottom: 80, left: 70, top: 40 }}
            >
            <BarPlot />
            <LinePlot />
            <MarkPlot />
            <ChartsXAxis label={t('techDebt.chart.xAxis')} axisId="x-axis" />
            <ChartsYAxis axisId="y-axis" />
            <ChartsLegend direction="horizontal" />
            <ChartsTooltip />
          </ChartContainer>
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ mt: 3 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('techDebt.details.title')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {data?.map((metric) => {
                const date = new Date(metric.date)
                const formattedDate = date.toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })

                return (
                  <Box key={metric.id} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ minWidth: 120 }}>
                      <strong>{formattedDate}:</strong>
                    </Typography>
                    <Typography variant="body2">Carryover: {metric.carryoverDebtPoints}</Typography>
                    <Typography variant="body2">Created: {metric.createdInYearPoints}</Typography>
                    <Typography variant="body2">Completed: {metric.completedInYearPoints}</Typography>
                    <Typography variant="body2">
                      <strong>Total Debt: {metric.totalDebt}</strong>
                    </Typography>
                  </Box>
                )
              })}
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  )
}

export default TechDebtTab
